import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X, Check, ArrowRight, AlertTriangle, Info, CalendarClock, Coins, Gift, DollarSign } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { resolveNotification, NotificationType } from '../lib/activity'
import { formatDistanceToNow } from 'date-fns'
import { hasAdminRights } from '../lib/roles'

type NotificationRow = {
  id: string
  type: NotificationType
  title: string
  message: string | null
  route: string | null
  severity: 'info' | 'warning' | 'alert'
  created_at: string
}

const POLL_MS = 30000

function typeVisual(type: NotificationType) {
  switch (type) {
    case 'session_approval': return { Icon: CalendarClock, tone: 'text-brand-500 bg-brand-50 dark:bg-brand-900/30' }
    case 'rate_request': return { Icon: Coins, tone: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30' }
    case 'consulting_followup': return { Icon: Gift, tone: 'text-amber-500 bg-amber-50 dark:bg-amber-900/30' }
    case 'topup': return { Icon: DollarSign, tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30' }
    case 'low_balance': return { Icon: AlertTriangle, tone: 'text-rose-600 bg-rose-50 dark:bg-rose-900/30' }
    default: return { Icon: Info, tone: 'text-gray-500 bg-gray-100 dark:bg-gray-800' }
  }
}

/**
 * Persistent admin notification bell. Shows unresolved notifications with a red
 * badge that ONLY clears when the admin marks each "Done". Each item offers a
 * "Go to task" deep-link to the page where it can be resolved.
 */
export function NotificationBell() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [rows, setRows] = useState<NotificationRow[]>([])
  const [open, setOpen] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [studentsAtZero, setStudentsAtZero] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  const isAdmin = hasAdminRights(profile?.role)

  const fetchNotifications = useCallback(async () => {
    if (!isAdmin) return
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, type, title, message, route, severity, created_at')
        .eq('is_resolved', false)
        .eq('target_role', 'admin')
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      setRows((data as NotificationRow[]) || [])
      // Bill later students are billed afterwards, so they don't count as out of minutes.
      const zero = await supabase
        .from('students')
        .select('id', { count: 'exact', head: true })
        .lte('balance_minutes', 0)
        .eq('bill_later', false)
      setStudentsAtZero(zero.count ?? 0)
    } catch (e) {
      console.error('Error fetching notifications:', e)
    }
  }, [isAdmin])

  useEffect(() => {
    if (!isAdmin) return
    fetchNotifications()
    const t = setInterval(fetchNotifications, POLL_MS)
    return () => clearInterval(t)
  }, [isAdmin, fetchNotifications])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!isAdmin) return null

  const count = rows.length

  const handleDone = async (id: string) => {
    setBusyId(id)
    // Optimistic removal.
    setRows((prev) => prev.filter((r) => r.id !== id))
    await resolveNotification(id, profile?.email)
    setBusyId(null)
  }

  const handleGoToTask = (row: NotificationRow) => {
    setOpen(false)
    if (row.route) navigate(row.route)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) fetchNotifications() }}
        title="Notifications"
        aria-label={`Notifications${count ? `, ${count} unresolved` : ''}`}
        className="relative flex items-center justify-center h-10 w-10 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-600 dark:text-gray-300 transition-colors"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white ring-2 ring-white dark:ring-surface-100">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[24rem] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 shadow-xl z-50 animate-fadeIn">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-brand-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Notifications {count > 0 && <span className="text-rose-500">({count})</span>}
              </h3>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[28rem] overflow-y-auto">
            {count === 0 ? (
              <div className="py-10 text-center px-6">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/20">
                  <Check className="h-6 w-6 text-emerald-500" />
                </div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">You're all caught up</p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">New tasks needing your attention will appear here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((row) => {
                  const { Icon, tone } = typeVisual(row.type)
                  return (
                    <li key={row.id} className="px-4 py-3">
                      <div className="flex gap-3">
                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${tone}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white leading-snug">{row.title}</p>
                          {row.message && (
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-snug">{row.message}</p>
                          )}
                          <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                            {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            {row.route && (
                              <button
                                onClick={() => handleGoToTask(row)}
                                className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-700 transition-colors"
                              >
                                Go to task
                                <ArrowRight className="h-3 w-3" />
                              </button>
                            )}
                            {row.type === 'session_approval' && studentsAtZero > 0 ? (
                              <span
                                className="text-[11px] text-rose-500"
                                title={`${studentsAtZero} student(s) at zero or below`}
                              >
                                Approve or reject to clear
                              </span>
                            ) : (
                              <button
                                onClick={() => handleDone(row.id)}
                                disabled={busyId === row.id}
                                className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-0 transition-colors disabled:opacity-50"
                              >
                                <Check className="h-3 w-3" />
                                Done
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
